// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useReducer } from 'react';
import { Box, ButtonProps, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
// NOTE:
// We keep a runtime namespace import (`ParticipantBindings`) and a separate type-only import
// (`ParticipantOnboardingRequest`) to avoid Vite/CJS-ESM interop issues seen with value imports.
// The type import is erased at build time, while runtime choice access stays on the namespace object,
// preventing "Cannot read properties of undefined" during render.
import * as ParticipantBindings from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Participant';
import {
  ClientOnboarderRequest,
  ClientOnboarderService,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/ClientOnboarder';
import {
  ComplianceService,
  ComplianceServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Compliance';
import { EndInvestorOnboardingOffer } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/EndInvestor';
import {
  InstrumentSecurityControlService,
  InstrumentSecurityControlServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/InstrumentSecurityControl';
import { EndInvestorOnboardingRequest } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Onboarding';
import { type ParticipantOnboardingRequest } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Participant';
import {
  RegistrarService,
  RegistrarServiceRequest,
} from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Registrar';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { ParticipantAgentDialog } from '../../../components/Dialog/ParticipantAgentDialog';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { NotificationBadge } from '../../../components/NotificationBadge';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useAcceptRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useAcceptRegistrarServiceRequest';
import useCancelRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useCancelRegistrarServiceRequest';
import useRejectRegistrarServiceRequest from '../../../hooks/mutations/registry/onboarding/useRejectRegistrarServiceRequest';
import useTerminateComplianceService from '../../../hooks/mutations/registry/onboarding/useTerminateComplianceService';
import useTerminateInstrumentSecurityControlService from '../../../hooks/mutations/registry/onboarding/useTerminateInstrumentSecurityControlService';
import useMutate from '../../../hooks/mutations/useMutate';
import { useCsvExport } from '../../../hooks/other/useCsvExport';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import { useClawbackReceiverDelegations } from '../../../hooks/queries/registry/clawback/useClawbackReceiverDelegations';
import useClientOnboarderService from '../../../hooks/queries/registry/onboarding/useClientOnboarderService';
import useClientOnboarderServiceRequests from '../../../hooks/queries/registry/onboarding/useClientOnboarderServiceRequests';
import useClientOnboarderServices from '../../../hooks/queries/registry/onboarding/useClientOnboarderServices';
import useComplianceService from '../../../hooks/queries/registry/onboarding/useComplianceService';
import useComplianceServiceRequests from '../../../hooks/queries/registry/onboarding/useComplianceServiceRequests';
import useComplianceServices from '../../../hooks/queries/registry/onboarding/useComplianceServices';
import useEndInvestorOnboardingOffers from '../../../hooks/queries/registry/onboarding/useEndInvestorOnboardingOffers';
import useEndInvestorOnboardingRequests from '../../../hooks/queries/registry/onboarding/useEndInvestorOnboardingRequests';
import useInstrumentSecurityControlService from '../../../hooks/queries/registry/onboarding/useInstrumentSecurityControlService';
import useInstrumentSecurityControlServiceRequests from '../../../hooks/queries/registry/onboarding/useInstrumentSecurityControlServiceRequests';
import useInstrumentSecurityControlServices from '../../../hooks/queries/registry/onboarding/useInstrumentSecurityControlServices';
import useParticipantOnboardingRequests from '../../../hooks/queries/registry/onboarding/useParticipantOnboardingRequests';
import { useParticipantServices } from '../../../hooks/queries/registry/onboarding/useParticipantServices';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useRegistrarServiceRequests from '../../../hooks/queries/registry/onboarding/useRegistrarServiceRequests';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import { useRegistrarHoldingOperationsDelegations } from '../../../hooks/queries/registry/registrar/useRegistrarHoldingOperationsDelegationsByParty';
import { when } from '../../../utils/array';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import {
  SUC_ACCEPT_REGISTRAR_REQUEST,
  SUC_ACC_CLIENT_ONBOARDER_SVC,
  SUC_ACC_COMPLIANCE_SVC,
  SUC_ACC_END_INVESTOR_ONBOARDING_OFFER,
  SUC_ACC_PARTICIPANT_ONBOARDING_REQ,
  SUC_CAN_COMPLIANCE_SVC,
  SUC_CAN_END_INVESTOR_ONBOARDING_OFFER,
  SUC_CAN_END_INVESTOR_ONBOARDING_REQUEST,
  SUC_ACC_END_INVESTOR_ONBOARDING_REQUEST,
  SUC_REJ_END_INVESTOR_ONBOARDING_REQUEST,
  SUC_CAN_PARTICIPANT_ONBOARDING_REQ,
  SUC_CAN_REGISTRAR_SVC,
  SUC_CAN_CLIENT_ONBOARDER_SVC,
  SUC_REJ_CLIENT_ONBOARDER_SVC,
  SUC_REJ_COMPLIANCE_SVC,
  SUC_REJ_END_INVESTOR_ONBOARDING_OFFER,
  SUC_REJ_PARTICIPANT_ONBOARDING_REQ,
  SUC_REJ_REGISTRAR_SVC,
  SUC_TER_COMPLIANCE_SVC,
  SUC_ACC_INSTRUMENT_SECURITY_CONTROL_SVC,
  SUC_CAN_INSTRUMENT_SECURITY_CONTROL_SVC,
  SUC_REJ_INSTRUMENT_SECURITY_CONTROL_SVC,
  SUC_TER_INSTRUMENT_SECURITY_CONTROL_SVC,
  ERR_ACCEPT_REGISTRAR_REQUEST,
  ERR_ACC_CLIENT_ONBOARDER_SVC,
  ERR_ACC_COMPLIANCE_SVC,
  ERR_ACC_END_INVESTOR_ONBOARDING_OFFER,
  ERR_ACC_PARTICIPANT_ONBOARDING_REQ,
  ERR_ACC_REGISTRAR_SVC,
  ERR_CAN_COMPLIANCE_SVC,
  ERR_CAN_END_INVESTOR_ONBOARDING_OFFER,
  ERR_CAN_END_INVESTOR_ONBOARDING_REQUEST,
  ERR_ACC_END_INVESTOR_ONBOARDING_REQUEST,
  ERR_REJ_END_INVESTOR_ONBOARDING_REQUEST,
  ERR_CAN_PARTICIPANT_ONBOARDING_REQ,
  ERR_CAN_REGISTRAR_SVC,
  ERR_CAN_CLIENT_ONBOARDER_SVC,
  ERR_REJ_CLIENT_ONBOARDER_SVC,
  ERR_REJ_COMPLIANCE_SVC,
  ERR_REJ_END_INVESTOR_ONBOARDING_OFFER,
  ERR_REJ_PARTICIPANT_ONBOARDING_REQ,
  ERR_REJ_REGISTRAR_SVC,
  ERR_TER_COMPLIANCE_SVC,
  ERR_ACC_INSTRUMENT_SECURITY_CONTROL_SVC,
  ERR_CAN_INSTRUMENT_SECURITY_CONTROL_SVC,
  ERR_REJ_INSTRUMENT_SECURITY_CONTROL_SVC,
  ERR_TER_INSTRUMENT_SECURITY_CONTROL_SVC,
} from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import OffboardClientOnboarderDialog from './OffboardClientOnboarderDialog';
import { OffboardEndInvestorDialog } from './OffboardEndInvestorDialog';
import { OffboardParticipantDialog } from './OffboardParticipantDialog';
import OffboardRegistrarDialog from './OffboardRegistrarDialog';
import { OfferEndInvestorOnboardingDialog } from './OfferEndInvestorOnboardingDialog';
import { RequestClientOnboarderServiceDialog } from './RequestClientOnboarderServiceDialog';
import { RequestComplianceServiceDialog } from './RequestComplianceServiceDialog';
import { RequestInstrumentSecurityControlServiceDialog } from './RequestInstrumentSecurityControlServiceDialog';
import { RequestParticipantOnboardingDialog } from './RequestParticipantOnboardingDialog';
import { RequestRegistrarServiceDialog } from './RequestRegistrarServiceDialog';
import { Row } from './types';

type Action = {
  name: string;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  action: (r: Row) => Promise<unknown>;
};

// Types for reject dialog state management
type AcceptableServiceType = 'RegistrarService' | 'ParticipantOnboardingRequest';

type AcceptDialogState = {
  isOpen: boolean;
  row: Row | null;
  serviceType: AcceptableServiceType | null;
};

type AcceptDialogAction =
  | { type: 'open'; row: Row; serviceType: AcceptableServiceType }
  | { type: 'close' };

const acceptDialogInitialState: AcceptDialogState = {
  isOpen: false,
  row: null,
  serviceType: null,
};

const acceptDialogReducer = (
  state: AcceptDialogState,
  action: AcceptDialogAction,
): AcceptDialogState => {
  switch (action.type) {
    case 'open':
      return { isOpen: true, row: action.row, serviceType: action.serviceType };
    case 'close':
      return acceptDialogInitialState;
    default:
      return state;
  }
};

type RejectableServiceType =
  | 'RegistrarService'
  | 'ClientOnboarderService'
  | 'ParticipantOnboardingRequest'
  | 'EndInvestorOnboardingOffer'
  | 'EndInvestorOnboardingRequest';

type RejectDialogState = {
  isOpen: boolean;
  row: Row | null;
  serviceType: RejectableServiceType | null;
};

type RejectDialogAction =
  | { type: 'open'; row: Row; serviceType: RejectableServiceType }
  | { type: 'close' };

const rejectDialogInitialState: RejectDialogState = {
  isOpen: false,
  row: null,
  serviceType: null,
};

const rejectDialogReducer = (
  state: RejectDialogState,
  action: RejectDialogAction,
): RejectDialogState => {
  switch (action.type) {
    case 'open':
      return { isOpen: true, row: action.row, serviceType: action.serviceType };
    case 'close':
      return rejectDialogInitialState;
    default:
      return state;
  }
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ variant, name, action, color }) => (
          <AsyncButton
            variant={variant}
            color={color}
            key={name}
            sx={{ mr: 1 }}
            onClick={() => action(params.row)}
          >
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'serviceType',
      headerName: 'Service Type',
      valueGetter: (params) => params.row.serviceType,
      flex: 150,
      filterable: true,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.user} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.user,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.operator} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.operator,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
      flex: 150,
      filterable: true,
      valueGetter: (params) => params.row.registrar,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      type: 'dateTime',
      valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
      renderCell: (params) => formatTimestamp(params.row.createdAt),
      flex: 150,
      minWidth: 180,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

interface OnboardingProps {
  disableVirtualization?: boolean;
}

export const Onboarding: React.FC<OnboardingProps> = ({ disableVirtualization }) => {
  const credentials = useCredentials();
  const clawbackDelegations = useClawbackReceiverDelegations();
  const registrarHoldingOperationsDelegations = useRegistrarHoldingOperationsDelegations();
  const [value, setValue] = React.useState('0');
  const onChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();

  const [rejectDialogState, dispatchRejectDialog] = useReducer(
    rejectDialogReducer,
    rejectDialogInitialState,
  );
  const [acceptDialogState, dispatchAcceptDialog] = useReducer(
    acceptDialogReducer,
    acceptDialogInitialState,
  );
  const { party } = useParties();
  const registrarService = useRegistrarService();
  const registrarServices = useRegistrarServices();
  const registrarServiceRequests = useRegistrarServiceRequests();
  const complianceService = useComplianceService();
  const instrumentSecurityControlService = useInstrumentSecurityControlService();
  const instrumentSecurityControlServices = useInstrumentSecurityControlServices();
  const instrumentSecurityControlServiceRequests = useInstrumentSecurityControlServiceRequests();
  const complianceServices = useComplianceServices();
  const complianceServiceRequests = useComplianceServiceRequests();
  const clientOnboarderService = useClientOnboarderService();
  const clientOnboarderServices = useClientOnboarderServices();
  const clientOnboarderServiceRequests = useClientOnboarderServiceRequests();
  const participantOnboardingRequests = useParticipantOnboardingRequests();
  const participantServices = useParticipantServices();
  const acceptRegistrarServiceRequest = useAcceptRegistrarServiceRequest();
  const endInvestorOnboardingOffers = useEndInvestorOnboardingOffers();
  const endInvestorOnboardingRequests = useEndInvestorOnboardingRequests();
  const rejectRegistrarServiceRequest = useRejectRegistrarServiceRequest();
  const cancelRegistrarServiceRequest = useCancelRegistrarServiceRequest();
  const terminateComplianceService = useTerminateComplianceService();
  const terminateInstrumentSecurityControlService = useTerminateInstrumentSecurityControlService();
  const acceptComplianceMutation = useLedgerExercise(
    ComplianceServiceRequest.ComplianceServiceRequest_Accept,
  );
  const rejectComplianceMutation = useLedgerExercise(
    ComplianceServiceRequest.ComplianceServiceRequest_Reject,
  );
  const cancelComplianceMutation = useLedgerExercise(
    ComplianceServiceRequest.ComplianceServiceRequest_Cancel,
  );
  const acceptInstrumentSecurityControlMutation = useLedgerExercise(
    InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Accept,
  );
  const rejectInstrumentSecurityControlMutation = useLedgerExercise(
    InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Reject,
  );
  const cancelInstrumentSecurityControlMutation = useLedgerExercise(
    InstrumentSecurityControlServiceRequest.InstrumentSecurityControlServiceRequest_Cancel,
  );
  const acceptClientOnboarderMutation = useLedgerExercise(
    RegistrarService.RegistrarService_AcceptClientOnboarderRequest,
  );
  const rejectClientOnboarderMutation = useLedgerExercise(
    ClientOnboarderRequest.ClientOnboarderOffer_Reject,
  );
  const cancelClientOnboarderMutation = useLedgerExercise(ClientOnboarderRequest.Archive);

  const acceptParticipantOnboardingRequestMutation = useLedgerExercise(
    ClientOnboarderService.ClientOnboarderService_AcceptParticipantOnboarding,
  );
  const rejectParticipantOnboardingRequestMutation = useLedgerExercise(
    ParticipantBindings.ParticipantOnboardingRequest.ParticipantOnboardingRequest_Reject,
  );
  const cancelParticipantOnboardingRequestMutation = useLedgerExercise(
    ParticipantBindings.ParticipantOnboardingRequest.Archive,
  );
  const acceptEndInvestorOnboardingOfferMutation = useLedgerExercise(
    EndInvestorOnboardingOffer.EndInvestorOnboardingOffer_Accept,
  );
  const rejectEndInvestorOnboardingOfferMutation = useLedgerExercise(
    EndInvestorOnboardingOffer.EndInvestorOnboardingOffer_Reject,
  );
  const cancelEndInvestorOnboardingOfferMutation = useLedgerExercise(
    EndInvestorOnboardingOffer.EndInvestorOnboardingOffer_Cancel,
  );
  const approveEndInvestorOnboardingRequestMutation = useLedgerExercise(
    ClientOnboarderService.ClientOnboarderService_ApproveEndInvestorOnboarding,
  );
  const rejectEndInvestorOnboardingRequestMutation = useLedgerExercise(
    ClientOnboarderService.ClientOnboarderService_RejectEndInvestorOnboarding,
  );
  const cancelEndInvestorOnboardingRequestMutation = useLedgerExercise(
    EndInvestorOnboardingRequest.EndInvestorOnboardingRequest_Cancel,
  );

  const [selectedRow] = React.useState<Row | undefined>();
  const [selectedClientOnboarderServiceContractId, setSelectedClientOnboarderServiceContractId] =
    React.useState<string | undefined>();
  const [selectedParticipant, setSelectedParticipant] = React.useState<
    { contractId: string; participant: string; registrar: string } | undefined
  >();
  const [selectedRegistrar, setSelectedRegistrar] = React.useState<
    { registrarServiceContractId: string; registrar: string } | undefined
  >();
  const [selectedClientOnboarder, setSelectedClientOnboarder] = React.useState<
    { clientOnboarderServiceContractId: string; clientOnboarder: string } | undefined
  >();
  // suppress unused warning in TS strict mode for selectedRow
  void selectedRow;

  const mutate = useMutate();

  const terminateCompliance = useCallback(
    (r: Row) => {
      const payload = { complianceServiceCid: r.contractId as ContractId<ComplianceService> };
      return mutate(
        terminateComplianceService,
        payload,
        SUC_TER_COMPLIANCE_SVC,
        ERR_TER_COMPLIANCE_SVC,
      );
    },
    [terminateComplianceService, mutate],
  );

  const { enqueueSnackbar } = useSnackbar();

  const acceptRegistrar = useCallback(
    (r: Row, participantAgent: string): Promise<void> => {
      if (!complianceService.data) {
        enqueueSnackbar(`${ERR_ACC_REGISTRAR_SVC}. Compliance service is not available.`, {
          variant: 'error',
        });
        return Promise.resolve();
      }
      if (!instrumentSecurityControlService.data) {
        enqueueSnackbar(
          `${ERR_ACC_REGISTRAR_SVC}. Instrument security control service is not available.`,
          {
            variant: 'error',
          },
        );
        return Promise.resolve();
      }
      const { compliance } = complianceService.data.payload;
      const { instrumentSecurityControl } = instrumentSecurityControlService.data.payload;
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
        compliance,
        instrumentSecurityControl,
        participantAgent,
      };
      return mutate(
        acceptRegistrarServiceRequest,
        payload,
        SUC_ACCEPT_REGISTRAR_REQUEST,
        ERR_ACCEPT_REGISTRAR_REQUEST,
      );
    },
    [
      complianceService.data,
      instrumentSecurityControlService.data,
      enqueueSnackbar,
      acceptRegistrarServiceRequest,
      mutate,
    ],
  );

  const rejectRegistrar = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
        rejectReason,
      };
      return mutate(
        rejectRegistrarServiceRequest,
        payload,
        SUC_REJ_REGISTRAR_SVC,
        ERR_REJ_REGISTRAR_SVC,
      );
    },
    [rejectRegistrarServiceRequest, mutate],
  );

  const cancelRegistrar = useCallback(
    (r: Row) => {
      const payload = {
        registrarServiceRequestCid: r.contractId as ContractId<RegistrarServiceRequest>,
      };
      return mutate(
        cancelRegistrarServiceRequest,
        payload,
        SUC_CAN_REGISTRAR_SVC,
        ERR_CAN_REGISTRAR_SVC,
      );
    },
    [cancelRegistrarServiceRequest, mutate],
  );

  const acceptCompliance = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<ComplianceServiceRequest>,
        argument: {},
      };
      return mutate(
        acceptComplianceMutation,
        payload,
        SUC_ACC_COMPLIANCE_SVC,
        ERR_ACC_COMPLIANCE_SVC,
      );
    },
    [acceptComplianceMutation, mutate],
  );

  const rejectCompliance = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<ComplianceServiceRequest>,
        argument: {},
      };
      return mutate(
        rejectComplianceMutation,
        payload,
        SUC_REJ_COMPLIANCE_SVC,
        ERR_REJ_COMPLIANCE_SVC,
      );
    },
    [rejectComplianceMutation, mutate],
  );

  const cancelCompliance = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<ComplianceServiceRequest>,
        argument: {},
      };
      return mutate(
        cancelComplianceMutation,
        payload,
        SUC_CAN_COMPLIANCE_SVC,
        ERR_CAN_COMPLIANCE_SVC,
      );
    },
    [cancelComplianceMutation, mutate],
  );

  const terminateInstrumentSecurityControl = useCallback(
    (r: Row) => {
      const payload = {
        instrumentSecurityControlServiceCid:
          r.contractId as ContractId<InstrumentSecurityControlService>,
      };
      return mutate(
        terminateInstrumentSecurityControlService,
        payload,
        SUC_TER_INSTRUMENT_SECURITY_CONTROL_SVC,
        ERR_TER_INSTRUMENT_SECURITY_CONTROL_SVC,
      );
    },
    [terminateInstrumentSecurityControlService, mutate],
  );

  const acceptInstrumentSecurityControl = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<InstrumentSecurityControlServiceRequest>,
        argument: {},
      };
      return mutate(
        acceptInstrumentSecurityControlMutation,
        payload,
        SUC_ACC_INSTRUMENT_SECURITY_CONTROL_SVC,
        ERR_ACC_INSTRUMENT_SECURITY_CONTROL_SVC,
      );
    },
    [acceptInstrumentSecurityControlMutation, mutate],
  );

  const rejectInstrumentSecurityControl = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<InstrumentSecurityControlServiceRequest>,
        argument: {},
      };
      return mutate(
        rejectInstrumentSecurityControlMutation,
        payload,
        SUC_REJ_INSTRUMENT_SECURITY_CONTROL_SVC,
        ERR_REJ_INSTRUMENT_SECURITY_CONTROL_SVC,
      );
    },
    [rejectInstrumentSecurityControlMutation, mutate],
  );

  const cancelInstrumentSecurityControl = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<InstrumentSecurityControlServiceRequest>,
        argument: {},
      };
      return mutate(
        cancelInstrumentSecurityControlMutation,
        payload,
        SUC_CAN_INSTRUMENT_SECURITY_CONTROL_SVC,
        ERR_CAN_INSTRUMENT_SECURITY_CONTROL_SVC,
      );
    },
    [cancelInstrumentSecurityControlMutation, mutate],
  );

  const rejectClientOnboarder = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        cid: r.contractId as ContractId<ClientOnboarderRequest>,
        argument: { reason: rejectReason },
      };
      return mutate(
        rejectClientOnboarderMutation,
        payload,
        SUC_REJ_CLIENT_ONBOARDER_SVC,
        ERR_REJ_CLIENT_ONBOARDER_SVC,
      );
    },
    [rejectClientOnboarderMutation, mutate],
  );

  const cancelClientOnboarder = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<ClientOnboarderRequest>,
        argument: {},
      };
      return mutate(
        cancelClientOnboarderMutation,
        payload,
        SUC_CAN_CLIENT_ONBOARDER_SVC,
        ERR_CAN_CLIENT_ONBOARDER_SVC,
      );
    },
    [cancelClientOnboarderMutation, mutate],
  );

  const acceptClientOnboarder = useCallback(
    (r: Row): Promise<void> => {
      if (!registrarService.data) {
        enqueueSnackbar(`${ERR_ACC_CLIENT_ONBOARDER_SVC}. Registrar service is not available.`, {
          variant: 'error',
        });
        return Promise.resolve();
      }
      const { compliance } = registrarService.data.payload;
      const payload = {
        cid: registrarService.data.contractId,
        argument: {
          clientOnboarderRequestCid: r.contractId as ContractId<ClientOnboarderRequest>,
          compliance,
        },
      };
      return mutate(
        acceptClientOnboarderMutation,
        payload,
        SUC_ACC_CLIENT_ONBOARDER_SVC,
        ERR_ACC_CLIENT_ONBOARDER_SVC,
      );
    },
    [registrarService, enqueueSnackbar, acceptClientOnboarderMutation, mutate],
  );

  const acceptParticipantOnboardingRequest = useCallback(
    (r: Row, participantAgent: string): Promise<void> => {
      if (!clientOnboarderService.data) {
        enqueueSnackbar(
          `${ERR_ACC_PARTICIPANT_ONBOARDING_REQ}. Client Onboarder service is not available.`,
          { variant: 'error' },
        );
        return Promise.resolve();
      }

      const payload = {
        cid: clientOnboarderService.data.contractId,
        argument: {
          request: r.contractId as ContractId<ParticipantOnboardingRequest>,
          existingParticipantServices: [],
          existingContracts: {
            existingCredentials: [],
            existingAllowLists: [],
            existingClawbackReceiverDelegations: [],
            existingRegistrarHoldingOperationsDelegations: [],
            existingOrchestratorHoldingOperationsDelegations: [],
            existingServiceUpgradeConsents: [],
          },
          participantAgent,
        },
      };

      return mutate(
        acceptParticipantOnboardingRequestMutation,
        payload,
        SUC_ACC_PARTICIPANT_ONBOARDING_REQ,
        ERR_ACC_PARTICIPANT_ONBOARDING_REQ,
      );
    },
    [
      clientOnboarderService.data,
      enqueueSnackbar,
      acceptParticipantOnboardingRequestMutation,
      mutate,
    ],
  );

  const rejectParticipantOnboardingRequest = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        cid: r.contractId as ContractId<ParticipantOnboardingRequest>,
        argument: { reason: rejectReason },
      };
      return mutate(
        rejectParticipantOnboardingRequestMutation,
        payload,
        SUC_REJ_PARTICIPANT_ONBOARDING_REQ,
        ERR_REJ_PARTICIPANT_ONBOARDING_REQ,
      );
    },
    [rejectParticipantOnboardingRequestMutation, mutate],
  );

  const cancelParticipantOnboardingRequest = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<ParticipantOnboardingRequest>,
        argument: {},
      };
      return mutate(
        cancelParticipantOnboardingRequestMutation,
        payload,
        SUC_CAN_PARTICIPANT_ONBOARDING_REQ,
        ERR_CAN_PARTICIPANT_ONBOARDING_REQ,
      );
    },
    [cancelParticipantOnboardingRequestMutation, mutate],
  );

  const acceptEndInvestorOnboardingOffer = useCallback(
    (r: Row): Promise<void> => {
      const payload = {
        cid: r.contractId as ContractId<EndInvestorOnboardingOffer>,
        argument: {},
      };
      return mutate(
        acceptEndInvestorOnboardingOfferMutation,
        payload,
        SUC_ACC_END_INVESTOR_ONBOARDING_OFFER,
        ERR_ACC_END_INVESTOR_ONBOARDING_OFFER,
      );
    },
    [acceptEndInvestorOnboardingOfferMutation, mutate],
  );

  const rejectEndInvestorOnboardingOffer = useCallback(
    (r: Row, rejectReason: string) => {
      const payload = {
        cid: r.contractId as ContractId<EndInvestorOnboardingOffer>,
        argument: { reason: rejectReason },
      };
      return mutate(
        rejectEndInvestorOnboardingOfferMutation,
        payload,
        SUC_REJ_END_INVESTOR_ONBOARDING_OFFER,
        ERR_REJ_END_INVESTOR_ONBOARDING_OFFER,
      );
    },
    [rejectEndInvestorOnboardingOfferMutation, mutate],
  );

  const cancelEndInvestorOnboardingOffer = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<EndInvestorOnboardingOffer>,
        argument: {},
      };
      return mutate(
        cancelEndInvestorOnboardingOfferMutation,
        payload,
        SUC_CAN_END_INVESTOR_ONBOARDING_OFFER,
        ERR_CAN_END_INVESTOR_ONBOARDING_OFFER,
      );
    },
    [cancelEndInvestorOnboardingOfferMutation, mutate],
  );

  const approveEndInvestorOnboardingRequest = useCallback(
    (r: Row): Promise<void> => {
      if (!clientOnboarderService.data) {
        enqueueSnackbar(
          `${ERR_ACC_END_INVESTOR_ONBOARDING_REQUEST}. Client Onboarder service is not available.`,
          { variant: 'error' },
        );
        return Promise.resolve();
      }

      const endInvestor = r.user;
      const participant = r.participant;

      const existingEndInvestorOnboardingRequests = (endInvestorOnboardingRequests.data ?? [])
        .filter((req) => req.contractId !== r.contractId)
        .map((req) => req.contractId);
      const existingEndInvestorOnboardingOffers = (endInvestorOnboardingOffers.data ?? []).map(
        (offer) => offer.contractId,
      );

      const existingCredentials = (credentials.data ?? [])
        .filter((c) => c.payload.holder === endInvestor && c.payload.participant === participant)
        .map((c) => c.contractId);
      const existingClawbackReceiverDelegations = (clawbackDelegations.data ?? [])
        .filter((d) => d.payload.delegator === endInvestor && d.payload.participant === participant)
        .map((d) => d.contractId);
      const existingRegistrarHoldingOperationsDelegations = (
        registrarHoldingOperationsDelegations.data ?? []
      )
        .filter((d) => d.payload.delegator === endInvestor && d.payload.participant === participant)
        .map((d) => d.contractId);

      const hasCredentialUnderDifferentParticipant = (credentials.data ?? []).some(
        (c) => c.payload.holder === endInvestor && c.payload.participant !== participant,
      );
      const hasOfferUnderDifferentParticipant = (endInvestorOnboardingOffers.data ?? []).some(
        (o) => o.payload.endInvestor === endInvestor && o.payload.participant !== participant,
      );

      const payload = {
        cid: clientOnboarderService.data.contractId,
        argument: {
          endInvestorOnboardingRequestCid: r.contractId as ContractId<EndInvestorOnboardingRequest>,
          endInvestorExistingContracts: {
            existingCredentials,
            existingClawbackReceiverDelegations,
            existingRegistrarHoldingOperationsDelegations,
            existingOrchestratorHoldingOperationsDelegations: [],
            existingServiceUpgradeConsents: [],
          },
          pendingEndInvestorOnboardingContracts: {
            endInvestorOnboardingRequestCids: existingEndInvestorOnboardingRequests,
            endInvestorOnboardingOfferCids: existingEndInvestorOnboardingOffers,
          },
          allowableOperation:
            !hasCredentialUnderDifferentParticipant && !hasOfferUnderDifferentParticipant,
        },
      };
      return mutate(
        approveEndInvestorOnboardingRequestMutation,
        payload,
        SUC_ACC_END_INVESTOR_ONBOARDING_REQUEST,
        ERR_ACC_END_INVESTOR_ONBOARDING_REQUEST,
      );
    },
    [
      clientOnboarderService.data,
      enqueueSnackbar,
      endInvestorOnboardingRequests.data,
      endInvestorOnboardingOffers.data,
      credentials.data,
      clawbackDelegations.data,
      registrarHoldingOperationsDelegations.data,
      approveEndInvestorOnboardingRequestMutation,
      mutate,
    ],
  );

  const rejectEndInvestorOnboardingRequest = useCallback(
    (r: Row, rejectReason: string): Promise<void> => {
      if (!clientOnboarderService.data) {
        enqueueSnackbar(
          `${ERR_REJ_END_INVESTOR_ONBOARDING_REQUEST}. Client Onboarder service is not available.`,
          { variant: 'error' },
        );
        return Promise.resolve();
      }
      const payload = {
        cid: clientOnboarderService.data.contractId,
        argument: {
          endInvestorOnboardingRequestCid: r.contractId as ContractId<EndInvestorOnboardingRequest>,
          reason: rejectReason,
        },
      };
      return mutate(
        rejectEndInvestorOnboardingRequestMutation,
        payload,
        SUC_REJ_END_INVESTOR_ONBOARDING_REQUEST,
        ERR_REJ_END_INVESTOR_ONBOARDING_REQUEST,
      );
    },
    [
      clientOnboarderService.data,
      enqueueSnackbar,
      rejectEndInvestorOnboardingRequestMutation,
      mutate,
    ],
  );

  const cancelEndInvestorOnboardingRequest = useCallback(
    (r: Row) => {
      const payload = {
        cid: r.contractId as ContractId<EndInvestorOnboardingRequest>,
        argument: {},
      };
      return mutate(
        cancelEndInvestorOnboardingRequestMutation,
        payload,
        SUC_CAN_END_INVESTOR_ONBOARDING_REQUEST,
        ERR_CAN_END_INVESTOR_ONBOARDING_REQUEST,
      );
    },
    [cancelEndInvestorOnboardingRequestMutation, mutate],
  );

  // Handle reject action based on stored state (row and service type)
  const handleReject = useCallback(
    (reason: string): Promise<void> => {
      const { row, serviceType } = rejectDialogState;
      if (!row || !serviceType) return Promise.resolve();

      switch (serviceType) {
        case 'RegistrarService':
          return rejectRegistrar(row, reason);
        case 'ClientOnboarderService':
          return rejectClientOnboarder(row, reason);
        case 'ParticipantOnboardingRequest':
          return rejectParticipantOnboardingRequest(row, reason);
        case 'EndInvestorOnboardingOffer':
          return rejectEndInvestorOnboardingOffer(row, reason);
        case 'EndInvestorOnboardingRequest':
          return rejectEndInvestorOnboardingRequest(row, reason);
        default:
          return Promise.resolve();
      }
    },
    [
      rejectDialogState,
      rejectRegistrar,
      rejectClientOnboarder,
      rejectParticipantOnboardingRequest,
      rejectEndInvestorOnboardingOffer,
      rejectEndInvestorOnboardingRequest,
    ],
  );

  // Handle accept action that requires participant agent input
  const handleAcceptWithParticipantAgent = useCallback(
    (participantAgent: string): Promise<void> => {
      const { row, serviceType } = acceptDialogState;
      if (!row || !serviceType) return Promise.resolve();

      switch (serviceType) {
        case 'RegistrarService':
          return acceptRegistrar(row, participantAgent);
        case 'ParticipantOnboardingRequest':
          return acceptParticipantOnboardingRequest(row, participantAgent);
        default:
          return Promise.resolve();
      }
    },
    [acceptDialogState, acceptRegistrar, acceptParticipantOnboardingRequest],
  );

  const createServiceActions = useCallback(
    (r: Row): Action[] => {
      switch (r.serviceType) {
        case 'RegistrarService':
          return when(r.operator === party, [
            {
              color: 'error',
              name: 'Offboard',
              action: () => {
                setSelectedRegistrar({
                  registrarServiceContractId: r.contractId,
                  registrar: r.registrar,
                });
                setDialogName('offboardRegistrar');
                return Promise.resolve();
              },
            },
          ]);

        case 'ComplianceService':
          return when(r.operator === party, [
            { color: 'error', name: 'Terminate', action: terminateCompliance },
          ]);

        case 'InstrumentSecurityControlService':
          return when(r.operator === party, [
            { color: 'error', name: 'Terminate', action: terminateInstrumentSecurityControl },
          ]);

        case 'ClientOnboarderService':
          return [
            ...when<Action>(r.registrar === party, [
              {
                color: 'error',
                name: 'Offboard',
                action: () => {
                  setSelectedClientOnboarder({
                    clientOnboarderServiceContractId: r.contractId,
                    clientOnboarder: r.user,
                  });
                  setDialogName('offboardClientOnboarder');
                  return Promise.resolve();
                },
              },
            ]),
            ...when<Action>(r.user === party, [
              {
                name: 'Offboard Participant',
                color: 'error',
                action: () => {
                  setSelectedClientOnboarderServiceContractId(r.contractId);
                  setDialogName('offboardParticipant');
                  return Promise.resolve();
                },
              },
            ]),
          ];

        case 'ParticipantService':
          return when(r.user === party, [
            {
              name: 'Request End Investor Onboarding',
              action: () => {
                setSelectedParticipant({
                  contractId: r.contractId,
                  participant: r.user,
                  registrar: r.registrar,
                });
                setDialogName('offerEndInvestorOnboarding');
                return Promise.resolve();
              },
            },
            {
              name: 'Offboard End Investor',
              color: 'error',
              action: () => {
                setSelectedParticipant({
                  contractId: r.contractId,
                  participant: r.user,
                  registrar: r.registrar,
                });
                setDialogName('endInvestorOffboarding');
                return Promise.resolve();
              },
            },
          ]);

        default:
          return [];
      }
    },
    [
      party,
      terminateCompliance,
      terminateInstrumentSecurityControl,
      setDialogName,
      setSelectedClientOnboarderServiceContractId,
      setSelectedClientOnboarder,
      setSelectedRegistrar,
    ],
  );

  // Memoize columns and serviceRows for CSV export (must be before early returns)
  const serviceColumns = useMemo(() => createHeaders(createServiceActions), [createServiceActions]);

  const serviceRows = useMemo(() => {
    return (registrarServices.data ?? [])
      .map<Row>((r) => ({
        contractId: r.contractId,
        serviceType: 'RegistrarService',
        operator: r.payload.operator,
        registrar: r.payload.registrar,
        user: r.payload.registrar,
        createdAt: r.createdAt,
      }))
      .concat(
        (complianceServices.data ?? []).map((r) => ({
          contractId: r.contractId,
          serviceType: 'ComplianceService',
          operator: r.payload.operator,
          registrar: '',
          user: r.payload.compliance,
          createdAt: r.createdAt,
        })),
      )
      .concat(
        (instrumentSecurityControlServices.data ?? []).map((r) => ({
          contractId: r.contractId,
          serviceType: 'InstrumentSecurityControlService',
          operator: r.payload.operator,
          registrar: '',
          user: r.payload.instrumentSecurityControl,
          createdAt: r.createdAt,
        })),
      )
      .concat(
        (clientOnboarderServices.data ?? []).map((r) => ({
          contractId: r.contractId,
          serviceType: 'ClientOnboarderService',
          operator: r.payload.operator,
          registrar: r.payload.registrar,
          user: r.payload.clientOnboarder,
          createdAt: r.createdAt,
        })),
      )
      .concat(
        (participantServices.data ?? []).map((r) => ({
          contractId: r.contractId,
          serviceType: 'ParticipantService',
          operator: r.payload.operator,
          registrar: r.payload.registrar,
          user: r.payload.participant,
          createdAt: r.createdAt,
        })),
      );
  }, [
    registrarServices.data,
    complianceServices.data,
    instrumentSecurityControlServices.data,
    clientOnboarderServices.data,
    participantServices.data,
  ]);

  const { handleExport: handleExportServices, isExportDisabled: isExportServicesDisabled } =
    useCsvExport(serviceColumns, serviceRows, {
      filename: 'services',
    });

  // Check if current user already has an active ParticipantService or pending request
  const isParticipantOnboardingDisabled = useMemo(() => {
    const hasActiveService = participantServices.data?.some(
      (svc) => svc.payload.participant === party,
    );
    const hasPendingRequest = participantOnboardingRequests.data?.some(
      (req) => req.payload.participant === party,
    );
    return hasActiveService || hasPendingRequest;
  }, [participantServices.data, participantOnboardingRequests.data, party]);

  const buttons = [
    ...(value === '0'
      ? [
          {
            disabled: isExportServicesDisabled,
            name: 'Export Services CSV',
            onClick: handleExportServices,
          },
        ]
      : []),
    {
      disabled: false,
      name: 'Request Compliance Service',
      onClick: () => setDialogName('requestComplianceService'),
    },
    {
      disabled: false,
      name: 'Request Instrument Security Control Service',
      onClick: () => setDialogName('requestInstrumentSecurityControlService'),
    },
    {
      disabled: false,
      name: 'Request Registrar Service',
      onClick: () => setDialogName('registrar'),
    },
    {
      disabled: !!registrarService.data,
      name: 'Request Client Onboarder Service',
      onClick: () => setDialogName('requestClientOnboarderService'),
    },
    {
      disabled: isParticipantOnboardingDisabled,
      name: 'Request Participant Onboarding',
      onClick: () => setDialogName('requestParticipantOnboarding'),
    },
  ];

  const createRequestActions = (r: Row): Action[] => {
    switch (r.serviceType) {
      case 'RegistrarService':
        return [
          ...when(r.operator === party, [
            {
              name: 'Accept',
              action: () => {
                dispatchAcceptDialog({ type: 'open', row: r, serviceType: 'RegistrarService' });
                return Promise.resolve();
              },
            },
            {
              name: 'Reject',
              action: () => {
                dispatchRejectDialog({ type: 'open', row: r, serviceType: 'RegistrarService' });
                return Promise.resolve();
              },
            },
          ]),
          ...when(r.user === party, [{ name: 'Cancel', action: cancelRegistrar }]),
        ];

      case 'ComplianceService':
        return [
          ...when(r.operator === party, [
            { name: 'Accept', action: acceptCompliance },
            { name: 'Reject', action: rejectCompliance },
          ]),
          ...when(r.user === party, [{ name: 'Cancel', action: cancelCompliance }]),
        ];

      case 'InstrumentSecurityControlService':
        return [
          ...when(r.operator === party, [
            { name: 'Accept', action: acceptInstrumentSecurityControl },
            { name: 'Reject', action: rejectInstrumentSecurityControl },
          ]),
          ...when(r.user === party, [{ name: 'Cancel', action: cancelInstrumentSecurityControl }]),
        ];

      case 'ClientOnboarderService':
        return [
          ...when(r.registrar === party, [
            { name: 'Accept', action: acceptClientOnboarder },
            {
              name: 'Reject',
              action: () => {
                dispatchRejectDialog({
                  type: 'open',
                  row: r,
                  serviceType: 'ClientOnboarderService',
                });
                return Promise.resolve();
              },
            },
          ]),
          ...when(r.user === party, [{ name: 'Cancel', action: cancelClientOnboarder }]),
        ];

      case 'ParticipantOnboardingRequest':
        return [
          ...when(r.clientOnboarder === party, [
            {
              name: 'Accept',
              action: () => {
                dispatchAcceptDialog({
                  type: 'open',
                  row: r,
                  serviceType: 'ParticipantOnboardingRequest',
                });
                return Promise.resolve();
              },
            },
            {
              name: 'Reject',
              action: () => {
                dispatchRejectDialog({
                  type: 'open',
                  row: r,
                  serviceType: 'ParticipantOnboardingRequest',
                });
                return Promise.resolve();
              },
            },
          ]),
          ...when(r.user === party, [
            { name: 'Cancel', action: cancelParticipantOnboardingRequest },
          ]),
        ];

      case 'EndInvestorOnboardingOffer':
        return [
          ...when(r.user === party, [
            { name: 'Accept', action: acceptEndInvestorOnboardingOffer },
            {
              name: 'Reject',
              action: () =>
                Promise.resolve(
                  dispatchRejectDialog({
                    type: 'open',
                    row: r,
                    serviceType: 'EndInvestorOnboardingOffer',
                  }),
                ),
            },
          ]),
          ...when(r.participant === party, [
            { name: 'Cancel', action: cancelEndInvestorOnboardingOffer },
          ]),
        ];

      case 'EndInvestorOnboardingRequest':
        return [
          ...when(r.clientOnboarder === party, [
            { name: 'Approve', action: approveEndInvestorOnboardingRequest },
            {
              name: 'Reject',
              action: () =>
                Promise.resolve(
                  dispatchRejectDialog({
                    type: 'open',
                    row: r,
                    serviceType: 'EndInvestorOnboardingRequest',
                  }),
                ),
            },
          ]),
          ...when(r.participant === party, [
            { name: 'Cancel', action: cancelEndInvestorOnboardingRequest },
          ]),
        ];

      default:
        return [];
    }
  };
  const isLoading =
    credentials.isLoading ||
    clawbackDelegations.isLoading ||
    registrarHoldingOperationsDelegations.isLoading ||
    registrarService.isLoading ||
    registrarServices.isLoading ||
    registrarServiceRequests.isLoading ||
    complianceService.isLoading ||
    instrumentSecurityControlService.isLoading ||
    complianceServices.isLoading ||
    complianceServiceRequests.isLoading ||
    instrumentSecurityControlServices.isLoading ||
    instrumentSecurityControlServiceRequests.isLoading ||
    clientOnboarderService.isLoading ||
    clientOnboarderServices.isLoading ||
    clientOnboarderServiceRequests.isLoading ||
    participantOnboardingRequests.isLoading ||
    endInvestorOnboardingOffers.isLoading ||
    endInvestorOnboardingRequests.isLoading ||
    participantServices.isLoading ||
    !credentials.data ||
    !clawbackDelegations.data ||
    !registrarHoldingOperationsDelegations.data ||
    !registrarServices.data ||
    !registrarServiceRequests.data ||
    !complianceServices.data ||
    !complianceServiceRequests.data ||
    !instrumentSecurityControlServices.data ||
    !instrumentSecurityControlServiceRequests.data ||
    !clientOnboarderServices.data ||
    !clientOnboarderServiceRequests.data ||
    !participantOnboardingRequests.data ||
    !endInvestorOnboardingOffers.data ||
    !endInvestorOnboardingRequests.data ||
    !participantServices.data;

  if (isLoading) return <Loading />;

  const requestRows = registrarServiceRequests.data
    .map<Row>((r) => ({
      contractId: r.contractId,
      serviceType: 'RegistrarService',
      operator: r.payload.operator,
      registrar: r.payload.registrar,
      user: r.payload.registrar,
      createdAt: r.createdAt,
    }))
    .concat(
      complianceServiceRequests.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'ComplianceService',
        operator: r.payload.operator,
        registrar: '',
        user: r.payload.compliance,
        createdAt: r.createdAt,
      })),
    )
    .concat(
      instrumentSecurityControlServiceRequests.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'InstrumentSecurityControlService',
        operator: r.payload.operator,
        registrar: '',
        user: r.payload.instrumentSecurityControl,
        createdAt: r.createdAt,
      })),
    )
    .concat(
      clientOnboarderServiceRequests.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'ClientOnboarderService',
        operator: r.payload.operator,
        registrar: r.payload.registrar,
        user: r.payload.clientOnboarder,
        createdAt: r.createdAt,
      })),
    )
    .concat(
      endInvestorOnboardingOffers.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'EndInvestorOnboardingOffer',
        operator: r.payload.operator,
        registrar: r.payload.registrar,
        user: r.payload.endInvestor,
        clientOnboarder: r.payload.clientOnboarder,
        participant: r.payload.participant,
        createdAt: r.createdAt,
      })),
    )
    .concat(
      endInvestorOnboardingRequests.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'EndInvestorOnboardingRequest',
        operator: r.payload.operator,
        registrar: r.payload.registrar,
        user: r.payload.endInvestor,
        clientOnboarder: r.payload.clientOnboarder,
        compliance: r.payload.compliance,
        participant: r.payload.participant,
        createdAt: r.createdAt,
      })),
    )
    .concat(
      participantOnboardingRequests.data.map((r) => ({
        contractId: r.contractId,
        serviceType: 'ParticipantOnboardingRequest',
        operator: r.payload.operator,
        registrar: r.payload.registrar,
        user: r.payload.participant,
        clientOnboarder: r.payload.clientOnboarder,
        createdAt: r.createdAt,
      })),
    );

  return (
    <PageWrapper buttons={buttons} pageName="Onboarding">
      {rejectDialogState.isOpen && (
        <RejectDialog
          open={rejectDialogState.isOpen}
          onClose={() => dispatchRejectDialog({ type: 'close' })}
          onReject={handleReject}
        />
      )}
      {acceptDialogState.isOpen && (
        <ParticipantAgentDialog
          open={acceptDialogState.isOpen}
          onClose={() => dispatchAcceptDialog({ type: 'close' })}
          onSubmit={handleAcceptWithParticipantAgent}
        />
      )}

      <Tabs value={value} onChange={onChange}>
        <Tab sx={{ textTransform: 'capitalize' }} value="0" label="services" />
        <Tab
          value="1"
          label={
            <Box
              sx={{
                textTransform: 'capitalize',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                Requests
              </Typography>
              <NotificationBadge value={requestRows.length || 0} />
            </Box>
          }
        />
      </Tabs>

      {dialogName === 'registrar' && <RequestRegistrarServiceDialog open onClose={onCloseDialog} />}
      {dialogName === 'requestComplianceService' && (
        <RequestComplianceServiceDialog open onClose={onCloseDialog} />
      )}
      {dialogName === 'requestInstrumentSecurityControlService' && (
        <RequestInstrumentSecurityControlServiceDialog open onClose={onCloseDialog} />
      )}
      {dialogName === 'requestClientOnboarderService' && (
        <RequestClientOnboarderServiceDialog open onClose={onCloseDialog} />
      )}
      {dialogName === 'requestParticipantOnboarding' && (
        <RequestParticipantOnboardingDialog open onClose={onCloseDialog} />
      )}
      {dialogName === 'offboardParticipant' && selectedClientOnboarderServiceContractId && (
        <OffboardParticipantDialog
          open
          onClose={() => {
            setSelectedClientOnboarderServiceContractId(undefined);
            onCloseDialog();
          }}
          clientOnboarderServiceContractId={selectedClientOnboarderServiceContractId}
        />
      )}
      {dialogName === 'offboardRegistrar' && selectedRegistrar && (
        <OffboardRegistrarDialog
          open
          onClose={() => {
            setSelectedRegistrar(undefined);
            onCloseDialog();
          }}
          registrarServiceContractId={selectedRegistrar.registrarServiceContractId}
          registrar={selectedRegistrar.registrar}
        />
      )}
      {dialogName === 'offboardClientOnboarder' && selectedClientOnboarder && (
        <OffboardClientOnboarderDialog
          open
          onClose={() => {
            setSelectedClientOnboarder(undefined);
            onCloseDialog();
          }}
          clientOnboarderServiceContractId={
            selectedClientOnboarder.clientOnboarderServiceContractId
          }
          clientOnboarder={selectedClientOnboarder.clientOnboarder}
        />
      )}
      {dialogName === 'offerEndInvestorOnboarding' && selectedParticipant && (
        <OfferEndInvestorOnboardingDialog
          open
          onClose={() => {
            setSelectedParticipant(undefined);
            onCloseDialog();
          }}
          participantServiceContractId={selectedParticipant.contractId}
          participant={selectedParticipant.participant}
        />
      )}
      {dialogName === 'endInvestorOffboarding' && selectedParticipant && (
        <OffboardEndInvestorDialog
          open
          onClose={() => {
            setSelectedParticipant(undefined);
            onCloseDialog();
          }}
          participantServiceContractId={selectedParticipant.contractId}
          participant={selectedParticipant.participant}
        />
      )}
      {value === '0' && (
        <DataTable
          columns={serviceColumns}
          rows={serviceRows}
          rowKey={(a) => a.contractId}
          disableVirtualization={disableVirtualization}
        />
      )}
      {value === '1' && (
        <DataTable
          columns={createHeaders(createRequestActions)}
          rows={requestRows}
          rowKey={(a) => a.contractId}
          disableVirtualization={disableVirtualization}
        />
      )}
    </PageWrapper>
  );
};
